import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import IdleTimeout from '../utils/idleTimeout';
import debugLogger from '../utils/debug';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [idleTimeout, setIdleTimeout] = useState(null);
  const idleTimeoutRef = useRef(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    const initAuth = async () => {
      if (storedToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          debugLogger.log('Email verified:', userData.email_verified);
          if (userData.email_verified) {
            setToken(storedToken);
            setUser(userData);
            startIdleTimeout();
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
            const meRes = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: { 'Authorization': `Bearer ${storedToken}` }
            });
            if (meRes.ok) {
              const meData = await meRes.json();
              const updatedUser = { ...userData, ...meData.user };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } else if (meRes.status === 401) {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Error parsing stored user data or fetching /me:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();

    // Listen for storage changes (when localStorage is cleared externally)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue === null) {
        // Silent auth state clearing
        clearAuthState();
      }
    };

    // Listen for custom auth logout event
    const handleAuthLogout = () => {
      // Silent auth state clearing
      clearAuthState();
    };

    // Listen for user activity events (from API calls)
    const handleUserActivity = () => {
      if (idleTimeoutRef.current) {
        // Silent reset - no console logging to avoid spam
        idleTimeoutRef.current.reset();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth:logout', handleAuthLogout);
    window.addEventListener('auth:activity', handleUserActivity);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:logout', handleAuthLogout);
      window.removeEventListener('auth:activity', handleUserActivity);
      if (idleTimeoutRef.current) {
        idleTimeoutRef.current.stop();
      }
    };
  }, []); // Remove idleTimeout from dependencies to prevent infinite loop

  const login = async (email, password) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Double-check that the user's email is verified before logging them in
        if (data.user && data.user.email_verified) {
          debugLogger.log('AuthContext: Login successful');
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setToken(data.token);
          setUser(data.user);
          startIdleTimeout(); // Start monitoring user activity
          return { success: true };
        } else {
          debugLogger.log('AuthContext: Login failed - email not verified');
          return { success: false, error: 'Email not verified' };
        }
      } else {
        debugLogger.log('AuthContext: Login failed:', data.message);
        return {
          success: false,
          error: data.message || 'Login failed',
          requiresVerification: data.requiresVerification === true,
          userId: data.userId || null
        };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (username, email, phone, country, password, referralCode = null) => {
    try {
      console.log('🔵 AuthContext: Starting registration for:', email);
      console.log('🔗 AuthContext: Referral code:', referralCode);
      console.log('🌐 AuthContext: API URL:', import.meta.env.VITE_API_URL || 'http://localhost:5000/api');
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const requestBody = { username, email, phone, country, password };
      if (referralCode) {
        requestBody.referralCode = referralCode;
        console.log('✅ AuthContext: Referral code added to request body');
      } else {
        console.log('⚠️ AuthContext: No referral code to send');
      }
      console.log('📤 AuthContext: Request body:', JSON.stringify(requestBody, null, 2));
      console.log('AuthContext: Full URL:', `${API_BASE_URL}/auth/register`);
      
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log('AuthContext: Registration response:', data);
      console.log('AuthContext: Response status:', response.status);
      console.log('AuthContext: Response ok:', response.ok);
      console.log('AuthContext: requiresVerification:', data.requiresVerification);
      console.log('AuthContext: has token:', !!data.token);

      if (response.ok) {
        // For email verification flow, don't automatically log in the user
        if (data.requiresVerification) {
          console.log('AuthContext: Registration requires verification, not logging in');
          return { 
            success: true, 
            requiresVerification: true, 
            user: data.user,
            message: data.message 
          };
        } else {
          // Fallback for cases where verification is not required
          console.log('AuthContext: Registration successful, logging in user');
          console.log('AuthContext: WARNING - No requiresVerification flag, this should not happen with email verification enabled');
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setToken(data.token);
          setUser(data.user);
          return { success: true };
        }
      } else {
        console.log('AuthContext: Registration failed:', data.message);
        return { success: false, error: data.message || 'Registration failed' };
      }
    } catch (error) {
      console.log('AuthContext: Registration network error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    stopIdleTimeout(); // Stop monitoring user activity
  };

  const clearAuthState = () => {
    setToken(null);
    setUser(null);
    if (idleTimeoutRef.current) {
      idleTimeoutRef.current.stop();
      idleTimeoutRef.current = null;
    }
  };

  const startIdleTimeout = () => {
    if (idleTimeoutRef.current) {
      idleTimeoutRef.current.stop();
    }
    
    const timeout = new IdleTimeout(21600000, () => { // 6 hours = 21600000ms
      // Silent logout - no console logging
      logout();
    });
    
    timeout.start();
    idleTimeoutRef.current = timeout;
    setIdleTimeout(timeout);
  };

  const stopIdleTimeout = () => {
    if (idleTimeoutRef.current) {
      idleTimeoutRef.current.stop();
      idleTimeoutRef.current = null;
      setIdleTimeout(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const verifyEmail = async (token) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setToken(data.token);
        setUser(data.user);
        return {
          success: true,
          message: data.message,
          requiresPayment: data.requiresPayment === true,
          subscriptionExpiresAt: data.subscriptionExpiresAt || null
        };
      } else {
        const errorMessage = data.message || data.error || 'Email verification failed';
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('AuthContext: Verification network error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const resendVerification = async (userId) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Failed to resend verification email' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const getMe = async () => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const t = localStorage.getItem('token');
      if (!t) return null;
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${t}` }
      });
      if (!response.ok) return null;
      const data = await response.json();
      const updatedUser = { ...user, ...data.user };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error('AuthContext getMe error:', error);
      return null;
    }
  };

  const checkVerificationStatus = async (userId) => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/auth/verification-status/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.message || 'Failed to check verification status' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    clearAuthState,
    updateUser,
    getMe,
    verifyEmail,
    resendVerification,
    checkVerificationStatus,
    isAuthenticated: !!user && !!token,
    subscriptionActive: user?.subscriptionActive === true,
    subscriptionStatus: user?.subscriptionStatus ?? user?.subscription_status ?? 'pending',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 